import { useState, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Badge } from "../../components/ui/badge";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../../components/ui/dialog";
import { 
  Wallet, DollarSign, TrendingUp, Banknote, 
  ChevronLeft, ChevronRight, RefreshCw, ArrowUpRight
} from "lucide-react";
import { AgentLayout } from "./AgentDashboard";
import { API } from "../../App";
import axios from "axios";
import { toast } from "sonner";

export default function AgentMyCommissions() {
  const [loading, setLoading] = useState(true);
  const [balance, setBalance] = useState(null);
  const [commissions, setCommissions] = useState([]);
  const [withdrawals, setWithdrawals] = useState([]);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [withdrawDialogOpen, setWithdrawDialogOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [withdrawForm, setWithdrawForm] = useState({
    amount: "",
    bank_name: "",
    account_number: "",
    routing_number: "",
    account_holder_name: "",
    notes: ""
  });

  useEffect(() => {
    fetchData();
  }, [page]);

  const fetchData = async () => {
    const token = localStorage.getItem("agent_token");
    setLoading(true);
    try {
      const [balanceRes, commissionsRes, withdrawalsRes] = await Promise.all([
        axios.get(`${API}/agent/commissions/balance`, {
          headers: { Authorization: `Bearer ${token}` }
        }),
        axios.get(`${API}/agent/commissions`, {
          params: { page },
          headers: { Authorization: `Bearer ${token}` }
        }),
        axios.get(`${API}/agent/commission-withdrawals`, {
          headers: { Authorization: `Bearer ${token}` }
        })
      ]);
      setBalance(balanceRes.data);
      setCommissions(commissionsRes.data.commissions);
      setTotalPages(commissionsRes.data.pages);
      setWithdrawals(withdrawalsRes.data.withdrawals);
    } catch (error) {
      toast.error("Failed to load commission data");
    } finally {
      setLoading(false);
    }
  };

  const handleWithdraw = async (e) => {
    e.preventDefault();
    if (!withdrawForm.amount || !withdrawForm.bank_name || !withdrawForm.account_number) {
      toast.error("Please fill in all required fields");
      return;
    }

    const amount = parseFloat(withdrawForm.amount);
    if (amount > (balance?.balance || 0)) {
      toast.error("Insufficient balance");
      return;
    }

    setSubmitting(true);
    try {
      const token = localStorage.getItem("agent_token");
      await axios.post(`${API}/agent/commissions/withdraw`, {
        ...withdrawForm,
        amount
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Withdrawal request submitted");
      setWithdrawDialogOpen(false);
      setWithdrawForm({
        amount: "",
        bank_name: "",
        account_number: "",
        routing_number: "",
        account_holder_name: "",
        notes: ""
      });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to submit withdrawal");
    } finally {
      setSubmitting(false);
    }
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case "paid":
        return <Badge className="bg-green-100 text-green-800">Paid</Badge>;
      case "approved":
        return <Badge className="bg-blue-100 text-blue-800">Approved</Badge>;
      case "pending":
        return <Badge className="bg-amber-100 text-amber-800">Pending</Badge>;
      case "rejected":
        return <Badge className="bg-red-100 text-red-800">Rejected</Badge>;
      default:
        return <Badge variant="outline">{status}</Badge>;
    }
  };

  return (
    <AgentLayout title="My Commissions">
      <div className="space-y-6">
        <div className="flex justify-between items-center">
          <div>
            <h1 className="text-2xl font-bold" style={{ fontFamily: 'Young Serif, serif' }}>
              My Commissions
            </h1>
            <p className="text-muted-foreground">Track your earnings and request withdrawals</p>
          </div>
          <Button onClick={fetchData} variant="outline" disabled={loading}>
            <RefreshCw className={`h-4 w-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
        </div>

        {/* Balance Cards */}
        {balance && (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <Card className="dashboard-card border-2 border-primary">
              <CardContent className="pt-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-3xl font-bold text-primary">
                      ${balance.balance?.toFixed(2) || "0.00"}
                    </p>
                    <p className="text-sm text-muted-foreground">Available Balance</p>
                  </div>
                  <div className="bg-primary/10 p-3 rounded-lg">
                    <Wallet className="h-6 w-6 text-primary" />
                  </div>
                </div>
                <Button 
                  className="w-full mt-4" 
                  onClick={() => setWithdrawDialogOpen(true)}
                  disabled={!balance.balance || balance.balance <= 0}
                >
                  <ArrowUpRight className="h-4 w-4 mr-2" />
                  Request Withdrawal
                </Button>
              </CardContent>
            </Card>

            <Card className="dashboard-card">
              <CardContent className="pt-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-2xl font-bold text-green-600">
                      ${balance.total_earned?.toFixed(2) || "0.00"}
                    </p>
                    <p className="text-sm text-muted-foreground">Total Earned</p>
                  </div>
                  <div className="bg-green-100 p-3 rounded-lg">
                    <TrendingUp className="h-6 w-6 text-green-600" />
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className="dashboard-card">
              <CardContent className="pt-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-2xl font-bold text-blue-600">
                      ${balance.total_withdrawn?.toFixed(2) || "0.00"}
                    </p>
                    <p className="text-sm text-muted-foreground">Total Withdrawn</p>
                  </div>
                  <div className="bg-blue-100 p-3 rounded-lg">
                    <Banknote className="h-6 w-6 text-blue-600" />
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Withdrawals Section */}
        {withdrawals.length > 0 && (
          <Card>
            <CardHeader>
              <CardTitle>Withdrawal Requests</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                {withdrawals.slice(0, 5).map((w) => (
                  <div key={w.withdrawal_id} className="flex items-center justify-between p-3 bg-muted/50 rounded-lg">
                    <div>
                      <p className="font-medium">${w.amount?.toFixed(2)}</p>
                      <p className="text-xs text-muted-foreground">
                        {new Date(w.created_at).toLocaleDateString()}
                      </p>
                    </div>
                    {getStatusBadge(w.status)}
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Commissions Table */}
        <Card>
          <CardHeader>
            <CardTitle>Commission History</CardTitle>
            <CardDescription>Earnings from quotes and payments</CardDescription>
          </CardHeader>
          <CardContent>
            {loading ? (
              <div className="flex justify-center py-8">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
              </div>
            ) : commissions.length === 0 ? (
              <div className="text-center py-8 text-muted-foreground">
                <DollarSign className="h-12 w-12 mx-auto mb-2 opacity-50" />
                <p>No commissions yet</p>
                <p className="text-sm">Earn commissions by creating quotes that convert to policies!</p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b text-left">
                      <th className="pb-3 font-medium">Date</th>
                      <th className="pb-3 font-medium">Customer</th>
                      <th className="pb-3 font-medium">Source</th>
                      <th className="pb-3 font-medium">Payment</th>
                      <th className="pb-3 font-medium">Commission</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y">
                    {commissions.map((comm) => (
                      <tr key={comm.commission_id} className="hover:bg-muted/50">
                        <td className="py-3 text-sm">
                          {new Date(comm.created_at).toLocaleDateString()}
                        </td>
                        <td className="py-3 font-medium">{comm.customer_name}</td>
                        <td className="py-3">
                          <Badge variant="outline" className="capitalize">
                            {comm.source?.replace(/_/g, " ")}
                          </Badge>
                        </td>
                        <td className="py-3">${comm.payment_amount?.toFixed(2)}</td>
                        <td className="py-3 font-bold text-green-600">
                          +${comm.amount?.toFixed(2)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            {/* Pagination */}
            {totalPages > 1 && (
              <div className="flex justify-center gap-2 mt-4">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setPage(p => Math.max(1, p - 1))}
                  disabled={page === 1}
                >
                  <ChevronLeft className="h-4 w-4" />
                </Button>
                <span className="px-3 py-2 text-sm">
                  Page {page} of {totalPages}
                </span>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                  disabled={page === totalPages}
                >
                  <ChevronRight className="h-4 w-4" />
                </Button>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Withdrawal Dialog */}
      <Dialog open={withdrawDialogOpen} onOpenChange={setWithdrawDialogOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Request Withdrawal</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleWithdraw} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="amount">Amount *</Label>
              <div className="relative">
                <DollarSign className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  id="amount"
                  type="number"
                  step="0.01"
                  min="0"
                  max={balance?.balance || 0}
                  placeholder="0.00"
                  className="pl-10"
                  value={withdrawForm.amount}
                  onChange={(e) => setWithdrawForm(prev => ({ ...prev, amount: e.target.value }))}
                  required
                />
              </div>
              <p className="text-xs text-muted-foreground">
                Available: ${balance?.balance?.toFixed(2) || "0.00"}
              </p>
            </div>

            <div className="space-y-2">
              <Label htmlFor="bank_name">Bank Name *</Label>
              <Input
                id="bank_name"
                placeholder="e.g., Chase Bank"
                value={withdrawForm.bank_name}
                onChange={(e) => setWithdrawForm(prev => ({ ...prev, bank_name: e.target.value }))}
                required
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="account_number">Account Number *</Label>
                <Input
                  id="account_number"
                  placeholder="••••••••"
                  value={withdrawForm.account_number}
                  onChange={(e) => setWithdrawForm(prev => ({ ...prev, account_number: e.target.value }))}
                  required
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="routing_number">Routing Number</Label>
                <Input
                  id="routing_number"
                  placeholder="•••••••••"
                  value={withdrawForm.routing_number}
                  onChange={(e) => setWithdrawForm(prev => ({ ...prev, routing_number: e.target.value }))}
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="account_holder_name">Account Holder Name *</Label>
              <Input
                id="account_holder_name"
                placeholder="Full name on account"
                value={withdrawForm.account_holder_name}
                onChange={(e) => setWithdrawForm(prev => ({ ...prev, account_holder_name: e.target.value }))}
                required
              />
            </div>

            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setWithdrawDialogOpen(false)}>
                Cancel
              </Button>
              <Button type="submit" disabled={submitting}>
                {submitting ? "Submitting..." : "Submit Request"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </AgentLayout>
  );
}
